import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { BdAppEnvironmentService } from '@bindoc-projects/platform-lib/data';
import {
  AysCode,
  AysCodeCodingSystem,
  AysCodeHierarchyElementType,
  AysCodeResponse,
  AysCodes,
  AysDrgSpecificCode,
  AysDrgSpecificCodeMainDepartment,
  AysIcdSpecificCode,
  normalizeAvailableYears,
} from '@bindoc-projects/code-domain-lib';
import { forkJoin, Observable, of } from 'rxjs';
import { map, shareReplay, tap } from 'rxjs/operators';
import { AysCodesStore } from '../store/code.store';
import { AysCodeAgeGroupDistribution } from './age-group-distribution/age-group-distribution.model';
import { AysCodePrevalence } from './prevalence';
import { AysCodeCasesTimeSeries } from './cases-time-series';
import { CodePatientMigrationResponse } from './patient-migration';
import { BdAdministrativeDistrictId } from '@bindoc-projects/geographic-domain-lib';
import type { TreeNode } from 'primeng/api';
import { ParamMap, Params } from '@angular/router';
import { ApiEndpointKey } from '@bindoc-projects/app-config-domain-lib';
import { BdArrayUtil } from '@bindoc-projects/util-domain-lib';

/** Bounds the SQL `IN` list and the body of one bulk path request. */
const TREE_PATHS_CHUNK_SIZE = 1000;

@Injectable({
  providedIn: 'root',
})
export class AysCodeService {
  private _httpClient = inject(HttpClient);
  private _environmentService = inject(BdAppEnvironmentService);

  public static toQueryParams(codes: AysCode[]): Params {
    function toQueryParamString(code: AysCode): string {
      return `${code.codingSystem}--${code.hierarchyElementType}--${code.name}`;
    }

    return {
      codes: codes.map((it) => toQueryParamString(it)),
    };
  }

  public static fromQueryParams(params: ParamMap): AysCode[] {
    if (params.has('codes')) {
      return params.getAll('codes').map((codeTypeAndName) => {
        const parts = codeTypeAndName.split('--');
        return new AysCode(
          parts[2],
          undefined,
          parts[0] as AysCodeCodingSystem,
          parts[1] as AysCodeHierarchyElementType,
        );
      });
    } else {
      return [];
    }
  }

  private _codeCache: {
    [codingSystem in AysCodeCodingSystem]?: {
      [codeName: string]: Observable<
        AysCode | AysDrgSpecificCode | AysIcdSpecificCode
      >;
    };
  } = {};

  private _codeStore: AysCodesStore;

  constructor() {
    this._codeStore = new AysCodesStore(
      this._environmentService.load(ApiEndpointKey.AYS_CODE_URL),
    );
  }

  public get$(
    codingSystem: AysCodeCodingSystem,
    codeName: string,
    httpParams: HttpParams = new HttpParams(),
  ): Observable<AysCode | AysDrgSpecificCode | AysIcdSpecificCode> {
    // if http params were send, don`t use the cached value
    if (httpParams.keys().length > 0) {
      return this._get(codingSystem, codeName, httpParams);
    }

    if (this._codeCache[codingSystem]) {
      const cached = this._codeCache[codingSystem][codeName];
      if (cached) {
        return cached.pipe(map((it) => it.clone()));
      }
    } else {
      this._codeCache[codingSystem] = {};
    }

    // if no cache value exists, make a request and share this request with all
    // https://blog.thoughtram.io/angular/2018/03/05/advanced-caching-with-rxjs.html
    this._codeCache[codingSystem][codeName] = this._get(
      codingSystem,
      codeName,
      httpParams,
    ).pipe(
      map((it) => it.clone()),
      shareReplay(1),
    );

    return this._codeCache[codingSystem][codeName];
  }

  public getAll(
    codingSystem: AysCodeCodingSystem,
    codeNames: string[],
    year: number | null = null,
    httpParams: HttpParams = new HttpParams(),
  ): Observable<{ [codeName: string]: AysCode | AysDrgSpecificCode }> {
    const url = this._codeStore.codingSystem(codingSystem);
    const body: { codes: string[]; year: string | null } = {
      codes: codeNames,
      year: year?.toString() ?? null,
    };

    let result: Observable<{
      [codeName: string]: AysCode | AysDrgSpecificCode;
    }>;

    if (codingSystem === 'G-DRG') {
      result = this._httpClient
        .post<{
          [codeName: string]: {
            drgCode: AysCodeResponse;
            mainDepartment: AysDrgSpecificCodeMainDepartment;
          };
        }>(url, body, { params: httpParams })
        .pipe(
          map((codes) => {
            const result: { [codeName: string]: AysDrgSpecificCode } = {};

            Object.keys(codes).forEach((codeName) => {
              const code = codes[codeName];
              result[codeName] = new AysDrgSpecificCode(
                code.drgCode.name,
                code.drgCode.description,
                code.drgCode.year,
                normalizeAvailableYears(code.drgCode.availableYears),
                code.mainDepartment,
              );
            });
            return result;
          }),
        );
    } else {
      result = this._httpClient
        .post<{
          [codeName: string]: AysCodeResponse;
        }>(url, body, { params: httpParams })
        .pipe(
          map((codes) => {
            const result: { [codeName: string]: AysCode } = {};

            Object.keys(codes).forEach((codeName) => {
              result[codeName] = AysCode.fromObject(codes[codeName]);
            });

            return result;
          }),
        );
    }

    return result.pipe(
      tap((codeMap) => {
        Object.keys(codeMap).forEach((codeName) => {
          this._addToCache(codingSystem, codeName, codeMap[codeName]);
        });
      }),
    );
  }

  /**
   * Fetch all children for given codes.
   * If [numberOfDigits] is given, then all children with this numberOfDigits will be returned.
   *
   * Only the children codes, not the given codes will be returned !!!
   *
   * @param codingSystem
   * @param codes
   * @param numberOfDigits
   */
  public children(
    codingSystem: AysCodeCodingSystem,
    codes: AysCode[],
    numberOfDigits?: number,
  ): Observable<AysCode[]> {
    const url = this._codeStore.children(codingSystem);
    let body: {
      codes: AysCode[];
      numberOfDigits?: number;
    } = {
      codes,
    };

    if (numberOfDigits) {
      body = {
        codes,
        numberOfDigits,
      };
    }

    return this._httpClient
      .post<AysCodeResponse[]>(url, body)
      .pipe(map((data) => data.map(AysCode.fromObject)));
  }

  /**
   * Find codes by given criteria
   * @param codingSystem - Define the code type for the search
   * @param query - Some text, that should fit the name of the codes. If null, all codes will be returned
   * @param numberOfDigits - The number of digits, that all returned codes should have. If null, all codes will be returned
   * @param limit - The amount of codes, that should be returned. Default for backend api is 1_000
   * @param httpParams - Some special params if needed
   */
  public find(
    codingSystem: AysCodeCodingSystem,
    query: string,
    numberOfDigits?: number,
    limit?: number,
    year?: number | null,
    httpParams: HttpParams = new HttpParams(),
  ): Observable<AysCode[]> {
    const url = this._codeStore.codingSystem(codingSystem);

    if (query) {
      httpParams = httpParams.set('query', query);
    }

    if (numberOfDigits) {
      httpParams = httpParams.set('numberOfDigits', numberOfDigits.toString());
    }

    if (limit) {
      httpParams = httpParams.set('limit', limit);
    }

    if (year != null) {
      httpParams = httpParams.set('year', year.toString());
    }

    return this._httpClient
      .get<AysCodeResponse[]>(url, { params: httpParams })
      .pipe(
        map((codes) =>
          codes
            .map((it) => ({ ...it, codingSystem: codingSystem }))
            .map((it) => AysCode.fromObject(it)),
        ),
      );
  }

  public getAgeGroupDistribution(
    codingSystem: AysCodeCodingSystem,
    codeName: string,
    httpParams: HttpParams = new HttpParams(),
  ): Observable<AysCodeAgeGroupDistribution> {
    const url = this._codeStore.codeAgeGroupDistribution(
      codingSystem,
      codeName,
    );

    return this._httpClient
      .get(url, { params: httpParams })
      .pipe(
        map((res) =>
          AysCodeAgeGroupDistribution.fromObj(codingSystem, res as unknown),
        ),
      );
  }

  public prevalence(
    codingSystem: AysCodeCodingSystem,
    codeName: string,
    httpParams: HttpParams = new HttpParams(),
  ): Observable<AysCodePrevalence> {
    const url = this._codeStore.codePrevalence(codingSystem, codeName);

    return this._httpClient
      .get<{
        prevalencePerAdId: {
          [administrativeDistrictId: string]: {
            code: AysCodeResponse;
            administrativeDistrictId: string;
            year: string;
            population: number;
            caseCount: number;
            prevalencePer100Thousand: number;
            prevalencePer1Thousand: number;
          };
        };
        additionalInformation: { text: string };
      }>(url, { params: httpParams })
      .pipe(
        map((data) => {
          const result: AysCodePrevalence = {
            additionalInformation: data.additionalInformation,
            prevalencePerAdId: {},
          };

          Object.keys(data.prevalencePerAdId).forEach(
            (administrativeDistrictId) => {
              result.prevalencePerAdId[administrativeDistrictId] = {
                ...data.prevalencePerAdId[administrativeDistrictId],
                code: AysCode.fromObject(
                  data.prevalencePerAdId[administrativeDistrictId].code,
                ),
              };
            },
          );

          return result;
        }),
      );
  }

  public getCasesTimeSeries(
    codingSystem: AysCodeCodingSystem,
    codeName: string,
    httpParams: HttpParams = new HttpParams(),
  ): Observable<AysCodeCasesTimeSeries> {
    const url = `${this._codeStore.codeByCodeName(codingSystem, codeName)}/cases/timeseries`;
    return this._httpClient.get<AysCodeCasesTimeSeries>(url, {
      params: httpParams,
    });
  }

  public getPatientMigration(
    codingSystem: AysCodeCodingSystem,
    codeNames: string[],
    year?: number,
    administrativeDistrictIds?: BdAdministrativeDistrictId[],
  ): Observable<CodePatientMigrationResponse> {
    return this._fetchPatientMigration(
      codingSystem,
      codeNames,
      year,
      administrativeDistrictIds,
    );
  }

  public treePath(
    codingSystem: AysCodeCodingSystem,
    codeName: string,
    year?: number,
  ): Observable<TreeNode<AysCode>> {
    const url = this._codeStore.treeParent(codingSystem, codeName);
    const params =
      year != null ? new HttpParams().set('year', year.toString()) : undefined;

    return this._httpClient
      .get<TreePath>(url, { params })
      .pipe(map((obj) => this._pathToTreeNode(codingSystem, obj)));
  }

  /**
   * {@link treePath} for many codes in one request per chunk, keyed by code
   * name. Codes the backend does not know are missing from the map. Use this
   * whenever the names come from user data — a code spectrum holds hundreds.
   */
  public treePaths(
    codingSystem: AysCodeCodingSystem,
    codeNames: readonly string[],
    year?: number,
  ): Observable<Map<string, TreeNode<AysCode>>> {
    const names = [...new Set(codeNames)];
    if (names.length === 0) {
      return of(new Map());
    }

    const url = this._codeStore.treePaths(codingSystem);
    const requests = BdArrayUtil.chunking(names, TREE_PATHS_CHUNK_SIZE).map(
      (chunk) =>
        this._httpClient.post<Record<string, TreePath>>(url, {
          values: chunk,
          year: year?.toString(),
        }),
    );

    return forkJoin(requests).pipe(
      map(
        (responses) =>
          new Map(
            responses.flatMap((response) =>
              Object.entries(response).map(
                ([name, path]) =>
                  [name, this._pathToTreeNode(codingSystem, path)] as const,
              ),
            ),
          ),
      ),
    );
  }

  private _pathToTreeNode(
    codingSystem: AysCodeCodingSystem,
    path: TreePath,
  ): TreeNode<AysCode> {
    return {
      type: codingSystem,
      data: AysCodes.fromObject(path),
      parent:
        path.hasParent && path.parent
          ? this._pathToTreeNode(codingSystem, path.parent)
          : undefined,
      key: path.name,
      label: path.name,
      leaf: path.nodeType === 'external',
    };
  }

  public treeChildren(
    codeType: AysCodeCodingSystem,
    codeName?: string,
    year?: number,
  ): Observable<TreeNode<AysCode>[]> {
    const url = codeName
      ? this._codeStore.treeChildren(codeType)
      : this._codeStore.codeGroup(codeType);

    return codeName
      ? this._httpClient
          .post<{
            [codeName: string]: TreePath[];
          }>(url, { values: [codeName], year: year?.toString() })
          .pipe(
            map((response) => response[codeName]),
            map((codes) =>
              codes.map((code) => ({
                data: AysCodes.fromObject(code),
                type: code.codingSystem,
                key: code.name,
                label: `${code.name} - ${code.description}`,
                leaf: code.nodeType === 'external',
              })),
            ),
          )
      : this._httpClient.get<TreePath[]>(url).pipe(
          map((codes) =>
            codes.map((code) => ({
              data: AysCodes.fromObject(code),
              type: code.codingSystem,
              key: code.name,
              label: `${code.name} - ${code.description}`,
              leaf: code.nodeType === 'external',
            })),
          ),
        );
  }

  public static reverse(codeTree: TreeNode<AysCode>): TreeNode<AysCode> {
    if (codeTree.parent) {
      codeTree.parent.children = [codeTree];
      return this.reverse(codeTree.parent);
    } else {
      return codeTree;
    }
  }

  private _get(
    codeType: AysCodeCodingSystem,
    codeName: string,
    httpParams: HttpParams,
  ): Observable<AysCode | AysDrgSpecificCode | AysIcdSpecificCode> {
    const url = this._codeStore.codeByCodeName(codeType, codeName);
    return this._httpClient
      .get<AysCodeResponse | AysDrgSpecificCode | AysIcdSpecificCode>(url, {
        params: httpParams,
      })
      .pipe(map((response) => AysCodes.fromObject(response)));
  }

  private _fetchPatientMigration(
    codingSystem: AysCodeCodingSystem,
    codeNames: string[],
    year?: number,
    administrativeDistrictIds?: BdAdministrativeDistrictId[],
  ): Observable<CodePatientMigrationResponse> {
    const url = `${this._codeStore.codingSystem(codingSystem)}patient-migration`;
    const body: {
      codes: AysCodeResponse[];
      year?: string;
      administrativeDistrictIds?: number[];
    } = {
      year: year?.toString(),
      codes: codeNames.map((codeName) => {
        return {
          name: codeName,
          codingSystem,
          hierarchyElementType: 'specific',
        };
      }),
      administrativeDistrictIds: administrativeDistrictIds?.map((it) =>
        it.getNumericValue(),
      ),
    };
    return this._httpClient.post<CodePatientMigrationResponse>(url, body);
  }

  private _addToCache(
    codingSystem: AysCodeCodingSystem,
    codeName: string,
    code: AysCode,
  ) {
    if (!this._codeCache[codingSystem]) {
      this._codeCache[codingSystem] = {};
    }
    this._codeCache[codingSystem][codeName] = of(code);
  }
}

interface TreePath extends AysCodeResponse {
  nodeType: 'internal' | 'external';
  parent?: TreePath;
  hasParent?: boolean;
}
